import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, HeartPulse, Activity, RefreshCw, AlertTriangle, TrendingUp,
  TrendingDown, DollarSign, Shield, Home, Trophy, ExternalLink, Sparkles,
  CheckCircle2, XCircle, Info, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';
import StressResilienceCard from '../components/portfolio/StressResilienceCard';
import PortfolioAwareAdvisor from '../components/PortfolioAwareAdvisor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============= Formatting helpers =============

const fmtMoney = (v, opts = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const { compact = false, sign = false } = opts;
  const n = Number(v);
  const prefix = sign && n > 0 ? '+' : '';
  if (compact) {
    if (Math.abs(n) >= 1e6) return `${prefix}$${(n / 1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return `${prefix}$${(n / 1e3).toFixed(0)}K`;
  }
  return `${prefix}$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtPct = (v, digits = 1) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return `${Number(v).toFixed(digits)}%`;
};

// ============= Status → color mapping =============

const STATUS_STYLES = {
  healthy: {
    ring: '#22c55e', text: 'text-emerald-500', bg: 'bg-emerald-500/10',
    dot: 'bg-emerald-500', border: 'border-emerald-500/30',
  },
  needs_attention: {
    ring: '#eab308', text: 'text-yellow-500', bg: 'bg-yellow-500/10',
    dot: 'bg-yellow-500', border: 'border-yellow-500/30',
  },
  at_risk: {
    ring: '#f97316', text: 'text-orange-500', bg: 'bg-orange-500/10',
    dot: 'bg-orange-500', border: 'border-orange-500/30',
  },
  critical: {
    ring: '#ef4444', text: 'text-red-500', bg: 'bg-red-500/10',
    dot: 'bg-red-500', border: 'border-red-500/30',
  },
};

const SEVERITY_ICON = {
  green: { icon: CheckCircle2, cls: 'text-emerald-500' },
  yellow: { icon: AlertTriangle, cls: 'text-yellow-500' },
  red: { icon: XCircle, cls: 'text-red-500' },
  gray: { icon: Info, cls: 'text-slate-400' },
};

const ROW_STATUS_STYLES = {
  excellent: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  healthy: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  watch: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  weak: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

// ============= Radial score =============

function ScoreRing({ score, size = 220, strokeWidth = 14, status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.critical;
  const radius = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * radius;
  const progress = ((score || 0) / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor"
          className="text-slate-200 dark:text-slate-700" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={style.ring}
          strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={`${progress} ${circ}`}
          style={{ filter: `drop-shadow(0 0 12px ${style.ring}60)`, transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-6xl font-primary font-extrabold tracking-tight" style={{ color: style.ring }}>
          {score ?? '—'}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mt-1">
          / 100
        </span>
      </div>
    </div>
  );
}

// ============= Small reusable pieces =============

function KpiCard({ label, value, sub, tone = 'default', testId }) {
  const toneCls =
    tone === 'positive' ? 'text-emerald-500' :
    tone === 'negative' ? 'text-red-500' :
    'text-[#12355B] dark:text-white';
  return (
    <div
      data-testid={testId}
      className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-shadow duration-300"
    >
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">{label}</p>
      <p className={`mt-2 text-2xl font-primary font-extrabold tracking-tight ${toneCls}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, accent = '#45C04A' }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}15` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div>
        <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ============= Paywall (Free / Pro users) =============

function Paywall({ t, navigate }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
      <div
        data-testid="portfolio-doctor-paywall"
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#12355B] via-[#1565C0] to-[#12355B] text-white p-10 shadow-2xl"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle at 85% 15%, rgba(255,215,0,0.6) 0%, transparent 50%)' }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
              <HeartPulse className="w-7 h-7 text-[#FFD700]" />
            </div>
            <div>
              <span className="inline-block px-2.5 py-0.5 text-[10px] font-bold tracking-wider rounded-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B]">
                {t('portfolioDoctor.paywall.eyebrow')}
              </span>
              <h1 className="mt-2 text-3xl sm:text-4xl font-primary font-extrabold tracking-tight">
                {t('portfolioDoctor.paywall.title')}
              </h1>
            </div>
          </div>
          <p className="text-white/70 text-base max-w-lg mb-8">
            {t('portfolioDoctor.paywall.subtitle')}
          </p>
          <ul className="space-y-3 mb-8">
            {['feature1', 'feature2', 'feature3', 'feature4', 'feature5', 'feature6'].map((k) => (
              <li key={k} className="flex items-center gap-3 text-sm text-white/90">
                <div className="w-6 h-6 rounded-full bg-[#45C04A]/20 border border-[#45C04A]/40 flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#45C04A]" />
                </div>
                {t(`portfolioDoctor.paywall.${k}`)}
              </li>
            ))}
          </ul>
          <Button
            data-testid="portfolio-doctor-upgrade-btn"
            onClick={() => navigate('/pricing')}
            className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B] rounded-xl px-8 py-3 font-bold hover:-translate-y-[1px] transition-all duration-300 shadow-lg"
          >
            <Lock className="w-4 h-4 mr-2" />
            {t('portfolioDoctor.paywall.cta')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============= Main page =============

export default function PortfolioDoctor() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [sortKey, setSortKey] = useState('cashFlow');

  const load = async () => {
    try {
      const [uResp, dResp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/portfolio-doctor`, { withCredentials: true }),
      ]);
      setUser(uResp.data);
      setData(dResp.data);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate('/');
        return;
      }
      if (e.response?.status === 403) {
        // Load user separately so we can show the paywall gracefully.
        try {
          const uResp = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
          setUser(uResp.data);
        } catch { /* ignored */ }
        setError('premium_required');
        return;
      }
      setError('load_failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/portfolio-doctor/refresh`, {}, { withCredentials: true });
      setData(resp.data);
      toast.success(t('portfolioDoctor.analyzedNow'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.saveFailed', 'Failed to refresh'));
    } finally {
      setRefreshing(false);
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.property_performance) return [];
    const key = {
      cashFlow: 'monthly_cash_flow',
      roi: 'roi',
      capRate: 'cap_rate',
      equity: 'equity',
      ltv: 'ltv',
      value: 'current_value',
    }[sortKey];
    return [...data.property_performance].sort((a, b) => {
      const av = a[key] ?? -Infinity;
      const bv = b[key] ?? -Infinity;
      return bv - av;
    });
  }, [data, sortKey]);

  // ---------- LOADING ----------
  if (loading) {
    return (
      <PageShell t={t} navigate={navigate}>
        <div className="flex flex-col items-center justify-center py-32">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#45C04A] to-[#65D95A] flex items-center justify-center animate-pulse mb-4">
            <HeartPulse className="w-7 h-7 text-white" />
          </div>
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#45C04A]" />
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t('portfolioDoctor.loading')}</p>
        </div>
      </PageShell>
    );
  }

  // ---------- PAYWALL ----------
  if (error === 'premium_required') {
    return (
      <PageShell t={t} navigate={navigate}>
        <Paywall t={t} navigate={navigate} />
      </PageShell>
    );
  }

  // ---------- EMPTY ----------
  if (!data || data.empty) {
    return (
      <PageShell t={t} navigate={navigate}>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <HeartPulse className="w-8 h-8 text-slate-400" />
          </div>
          <h2 data-testid="portfolio-doctor-empty-title" className="text-2xl font-primary font-bold text-[#12355B] dark:text-white mb-2">
            {t('portfolioDoctor.empty.title')}
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{t('portfolioDoctor.empty.subtitle')}</p>
          <Button
            data-testid="portfolio-doctor-analyze-cta"
            onClick={() => navigate('/analyze')}
            className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-8 font-semibold"
          >
            {t('portfolioDoctor.empty.cta')}
          </Button>
        </div>
      </PageShell>
    );
  }

  // ---------- MAIN ----------
  const status = data.health_status?.key || 'critical';
  const statusStyle = STATUS_STYLES[status];
  const score = data.portfolio_health_score;
  const summaryKey = {
    healthy: 'summaryHealthy',
    needs_attention: 'summaryNeedsAttention',
    at_risk: 'summaryAtRisk',
    critical: 'summaryCritical',
  }[status];

  return (
    <PageShell t={t} navigate={navigate} refreshing={refreshing} onRefresh={handleRefresh}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8">
        {/* ============= HERO ============= */}
        <div
          data-testid="portfolio-doctor-hero"
          className={`relative overflow-hidden rounded-3xl bg-white dark:bg-[#131824] border-2 ${statusStyle.border} shadow-card p-6 sm:p-8`}
        >
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{ backgroundImage: `radial-gradient(circle at 85% 15%, ${statusStyle.ring}30 0%, transparent 55%)` }}
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-8 items-center">
            <div className="flex justify-center lg:justify-start">
              <ScoreRing score={score} size={220} strokeWidth={14} status={status} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
                {t('portfolioDoctor.score.label')}
              </p>
              <div className="flex items-center gap-3 mb-4">
                <span className={`w-3 h-3 rounded-full ${statusStyle.dot} shadow-[0_0_0_4px_rgba(0,0,0,0.05)]`} />
                <h1
                  data-testid="portfolio-doctor-status"
                  className={`text-3xl sm:text-4xl font-primary font-extrabold tracking-tight ${statusStyle.text}`}
                >
                  {t(`portfolioDoctor.status.${status}`)}
                </h1>
              </div>
              <p className="text-slate-600 dark:text-slate-300 text-base max-w-xl leading-relaxed mb-6">
                {t(`portfolioDoctor.score.${summaryKey}`)}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="view-recommendations-btn"
                  onClick={() => document.getElementById('doctor-recs')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#12355B] hover:bg-[#1565C0] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-6 font-semibold"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('portfolioDoctor.score.viewRecommendations')}
                </Button>
                <span data-testid="analyzed-at" className="inline-flex items-center gap-2 text-xs text-slate-400 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Activity className="w-3.5 h-3.5" />
                  {t('portfolioDoctor.analyzedNow')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ============= STRESS RESILIENCE CARD =============
            Hypothetical stress resilience — kept intentionally separate from
            the Portfolio Health Score above (current health) so users see two
            distinct dimensions. */}
        <StressResilienceCard isPremium={true} showActions={true} />

        {/* ============= KEY METRICS ============= */}
        <div>
          <SectionHeader icon={Activity} title={t('portfolioDoctor.metrics.title')} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testId="metric-total-value" label={t('portfolioDoctor.metrics.totalValue')}
              value={fmtMoney(data.metrics.total_value, { compact: true })} />
            <KpiCard testId="metric-total-equity" label={t('portfolioDoctor.metrics.totalEquity')}
              value={fmtMoney(data.metrics.total_equity, { compact: true })} />
            <KpiCard testId="metric-total-debt" label={t('portfolioDoctor.metrics.totalDebt')}
              value={fmtMoney(data.metrics.total_debt, { compact: true })} />
            <KpiCard testId="metric-portfolio-ltv" label={t('portfolioDoctor.metrics.portfolioLtv')}
              value={fmtPct(data.metrics.portfolio_ltv, 0)} />
            <KpiCard testId="metric-monthly-cf" label={t('portfolioDoctor.metrics.monthlyCF')}
              value={fmtMoney(data.metrics.monthly_cash_flow, { sign: true })}
              tone={data.metrics.monthly_cash_flow >= 0 ? 'positive' : 'negative'} />
            <KpiCard testId="metric-annual-cf" label={t('portfolioDoctor.metrics.annualCF')}
              value={fmtMoney(data.metrics.annual_cash_flow, { sign: true })}
              tone={data.metrics.annual_cash_flow >= 0 ? 'positive' : 'negative'} />
            <KpiCard testId="metric-portfolio-roi" label={t('portfolioDoctor.metrics.portfolioROI')}
              value={data.metrics.portfolio_roi === null ? t('portfolioDoctor.metrics.insufficient') : fmtPct(data.metrics.portfolio_roi)} />
            <KpiCard testId="metric-portfolio-cap" label={t('portfolioDoctor.metrics.portfolioCap')}
              value={data.metrics.portfolio_cap_rate === null ? t('portfolioDoctor.metrics.insufficient') : fmtPct(data.metrics.portfolio_cap_rate)} />
          </div>
          <p className="mt-2 text-[10px] text-slate-400 flex items-center gap-1.5">
            <Info className="w-3 h-3" />
            {t('portfolioDoctor.metrics.estimateNote')}
          </p>
        </div>

        {/* ============= DIAGNOSIS ============= */}
        <div>
          <SectionHeader icon={Activity} title={t('portfolioDoctor.diagnosis.title')} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.diagnosis.map((d, i) => {
              const Cfg = SEVERITY_ICON[d.severity] || SEVERITY_ICON.gray;
              const Icon = Cfg.icon;
              const vals = { ...d.values };
              if (vals.amount !== undefined) {
                const withSign = d.key !== 'equity_position';
                vals.amount = fmtMoney(vals.amount, { sign: withSign });
              }
              return (
                <div
                  key={i}
                  data-testid={`diagnosis-${d.key}`}
                  className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-4 flex items-start gap-3 shadow-card"
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${Cfg.cls}`} />
                  <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                    {t(`portfolioDoctor.diagnosis.${d.key}`, vals)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ============= WEAKEST + STRONGEST ============= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {data.weakest_property && (
            <PropertyCard
              testId="weakest-property"
              variant="weak"
              badge={t('portfolioDoctor.weakest.badge')}
              title={t('portfolioDoctor.weakest.title')}
              property={data.weakest_property}
              impactLabel={
                data.weakest_property.portfolio_impact_percent
                  ? t('portfolioDoctor.weakest.impact', { percent: data.weakest_property.portfolio_impact_percent })
                  : null
              }
              cta={t('portfolioDoctor.weakest.cta')}
              onCta={() => navigate(`/deal/${data.weakest_property.deal_id}`)}
              t={t}
            />
          )}
          {data.strongest_property && (
            <PropertyCard
              testId="strongest-property"
              variant="strong"
              badge={t('portfolioDoctor.strongest.badge')}
              title={t('portfolioDoctor.strongest.title')}
              property={data.strongest_property}
              impactLabel={
                data.strongest_property.portfolio_contribution_percent
                  ? t('portfolioDoctor.strongest.contribution', { percent: data.strongest_property.portfolio_contribution_percent })
                  : null
              }
              cta={t('portfolioDoctor.strongest.cta')}
              onCta={() => navigate(`/deal/${data.strongest_property.deal_id}`)}
              t={t}
            />
          )}
        </div>

        {/* ============= PROPERTY PERFORMANCE TABLE ============= */}
        <div>
          <div className="flex items-end justify-between mb-5">
            <SectionHeader icon={Home} title={t('portfolioDoctor.table.title')} />
            <div className="flex items-center gap-2 -mt-1">
              <span className="text-xs text-slate-400">{t('portfolioDoctor.table.sortBy')}:</span>
              <select
                data-testid="performance-sort-select"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="border border-slate-200 dark:border-slate-700 dark:bg-[#131824] dark:text-white rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30"
              >
                <option value="cashFlow">{t('portfolioDoctor.table.sort.cashFlow')}</option>
                <option value="roi">{t('portfolioDoctor.table.sort.roi')}</option>
                <option value="capRate">{t('portfolioDoctor.table.sort.capRate')}</option>
                <option value="equity">{t('portfolioDoctor.table.sort.equity')}</option>
                <option value="ltv">{t('portfolioDoctor.table.sort.ltv')}</option>
                <option value="value">{t('portfolioDoctor.table.sort.value')}</option>
              </select>
            </div>
          </div>
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-card">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table data-testid="performance-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="text-left py-3 px-4 font-semibold">{t('portfolioDoctor.table.property')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.value')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.equity')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.debt')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.ltv')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.monthlyCF')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.roi')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.capRate')}</th>
                    <th className="text-right py-3 px-4 font-semibold">{t('portfolioDoctor.table.status')}</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((p) => (
                    <tr
                      key={p.deal_id}
                      data-testid={`performance-row-${p.deal_id}`}
                      className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                    >
                      <td className="py-3 px-4 max-w-[240px] truncate text-slate-700 dark:text-slate-200">{p.address}</td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{fmtMoney(p.current_value, { compact: true })}</td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{fmtMoney(p.equity, { compact: true })}</td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{fmtMoney(p.debt, { compact: true })}</td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{fmtPct(p.ltv, 0)}</td>
                      <td className={`py-3 px-4 text-right font-semibold ${(p.monthly_cash_flow || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {fmtMoney(p.monthly_cash_flow, { sign: true })}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{fmtPct(p.roi, 1)}</td>
                      <td className="py-3 px-4 text-right text-slate-700 dark:text-slate-300">{fmtPct(p.cap_rate, 1)}</td>
                      <td className="py-3 px-4 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROW_STATUS_STYLES[p.status]}`}>
                          {t(`portfolioDoctor.table.statusLabels.${p.status}`)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => navigate(`/deal/${p.deal_id}`)}
                          className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {sortedRows.map((p) => (
                <div key={p.deal_id} data-testid={`performance-card-${p.deal_id}`} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-semibold text-[#12355B] dark:text-white truncate max-w-[70%]">{p.address}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROW_STATUS_STYLES[p.status]}`}>
                      {t(`portfolioDoctor.table.statusLabels.${p.status}`)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <MobileCell label={t('portfolioDoctor.table.monthlyCF')}
                      value={fmtMoney(p.monthly_cash_flow, { sign: true })}
                      tone={(p.monthly_cash_flow || 0) >= 0 ? 'positive' : 'negative'} />
                    <MobileCell label={t('portfolioDoctor.table.roi')} value={fmtPct(p.roi, 1)} />
                    <MobileCell label={t('portfolioDoctor.table.capRate')} value={fmtPct(p.cap_rate, 1)} />
                    <MobileCell label={t('portfolioDoctor.table.equity')} value={fmtMoney(p.equity, { compact: true })} />
                    <MobileCell label={t('portfolioDoctor.table.debt')} value={fmtMoney(p.debt, { compact: true })} />
                    <MobileCell label={t('portfolioDoctor.table.ltv')} value={fmtPct(p.ltv, 0)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ============= CASH FLOW CONCENTRATION ============= */}
        <div>
          <SectionHeader
            icon={DollarSign}
            title={t('portfolioDoctor.concentration.title')}
            subtitle={t('portfolioDoctor.concentration.subtitle')}
          />
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card">
            {data.cash_flow_concentration.warning && (
              <div
                data-testid="cash-flow-warning"
                className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30"
              >
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t(`portfolioDoctor.concentration.warning`, data.cash_flow_concentration.warning.values)}
                </p>
              </div>
            )}
            {data.cash_flow_concentration.items.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                {t('portfolioDoctor.metrics.insufficient')}
              </p>
            ) : (
              <div className="space-y-3">
                {data.cash_flow_concentration.items.map((item, i) => (
                  <div key={item.deal_id} data-testid={`concentration-row-${item.deal_id}`} className="flex items-center gap-3">
                    <span className="text-xs w-6 text-slate-400 font-mono">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-slate-700 dark:text-slate-200 truncate max-w-[60%]">{item.address}</p>
                        <span className="text-xs font-semibold text-[#12355B] dark:text-white">{item.share_percent}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#45C04A] to-[#65D95A]"
                          style={{ width: `${Math.max(item.share_percent, 2)}%`, transition: 'width 0.8s ease' }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ============= DEBT + EQUITY ============= */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DebtCard t={t} data={data.debt_analysis} />
          <EquityCard t={t} data={data.equity_analysis} />
        </div>

        {/* ============= RECOMMENDATIONS ============= */}
        <div id="doctor-recs">
          <SectionHeader icon={Sparkles} title={t('portfolioDoctor.recs.title')} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.recommendations.length === 0 && (
              <p className="text-sm text-slate-400 italic col-span-2">—</p>
            )}
            {data.recommendations.map((r, i) => {
              const Cfg = SEVERITY_ICON[r.priority] || SEVERITY_ICON.gray;
              const Icon = Cfg.icon;
              return (
                <div
                  key={i}
                  data-testid={`recommendation-${r.key}`}
                  className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-4 flex items-start gap-3 shadow-card"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${Cfg.cls} bg-current/10`}>
                    <Icon className={`w-4 h-4 ${Cfg.cls}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mr-2">#{i + 1}</span>
                      {t(`portfolioDoctor.recs.${r.key}`, r.values)}
                    </p>
                    {r.deal_id && (
                      <button
                        onClick={() => navigate(`/deal/${r.deal_id}`)}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[#12355B] dark:text-[#45C04A] font-semibold hover:underline"
                      >
                        {t('portfolioDoctor.recs.viewProperty')}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Portfolio-Aware AI Advisor — grounded on the current diagnosis
          (score, category, weakest/strongest property, top vulnerabilities
          + top 5 recommendations). Backend enforces Premium capability.
          Recommendation titles + diagnosis codes are translated HERE so
          the LLM sees the same human-facing text the user is reading, and
          strongest/weakest are trimmed to just {address, monthly_cash_flow}
          to keep the injected block compact. */}
      <PortfolioAwareAdvisor
        contextType="portfolio_doctor"
        context={{
          health_score: score,
          health_category: status,
          diagnosis: (data.diagnosis || [])
            .map((d) => t(`portfolioDoctor.diagnosis.${d.key}`, d.values || {}))
            .filter(Boolean)
            .join(' · ') || undefined,
          strongest_property: data.strongest_property
            ? {
                address: data.strongest_property.address,
                monthly_cash_flow: data.strongest_property.monthly_cash_flow,
              }
            : undefined,
          weakest_property: data.weakest_property
            ? {
                address: data.weakest_property.address,
                monthly_cash_flow: data.weakest_property.monthly_cash_flow,
              }
            : undefined,
          vulnerabilities: (data.vulnerabilities || []).slice(0, 5),
          recommendations: (data.recommendations || []).slice(0, 5).map((r) => ({
            title: r.key ? t(`portfolioDoctor.recs.${r.key}`, r.values || {}) : (r.title || r.action),
            impact: r.impact_summary || r.impact,
          })),
        }}
      />
    </PageShell>
  );
}

// ============= Sub-components =============

function PageShell({ t, navigate, children, refreshing, onRefresh }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                data-testid="refresh-portfolio-btn"
                onClick={onRefresh}
                disabled={refreshing}
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-[#12355B] dark:hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? t('portfolioDoctor.refreshing') : t('portfolioDoctor.refresh')}
              </Button>
            )}
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
            <Button
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('nav.backToDashboard')}
            </Button>
          </div>
        </div>
      </nav>
      {children}
      <Footer />
    </div>
  );
}

function MobileCell({ label, value, tone }) {
  const toneCls =
    tone === 'positive' ? 'text-emerald-500' :
    tone === 'negative' ? 'text-red-500' :
    'text-[#12355B] dark:text-white';
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-xs font-bold ${toneCls}`}>{value}</p>
    </div>
  );
}

function PropertyCard({ testId, variant, badge, title, property, impactLabel, cta, onCta, t }) {
  const isStrong = variant === 'strong';
  const accent = isStrong ? '#45C04A' : '#ef4444';
  const Icon = isStrong ? Trophy : AlertTriangle;
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-2xl border-2 p-6 shadow-card bg-white dark:bg-[#131824] ${isStrong ? 'border-emerald-500/30' : 'border-red-500/30'}`}
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ backgroundImage: `radial-gradient(circle at 100% 0%, ${accent}25 0%, transparent 50%)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: `${accent}15` }}>
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div>
            <span className="text-[9px] font-bold tracking-[0.14em] uppercase" style={{ color: accent }}>
              {badge}
            </span>
            <h3 className="font-primary font-bold text-sm text-[#12355B] dark:text-white -mt-0.5">{title}</h3>
          </div>
        </div>
        <p className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-4 line-clamp-2">
          {property.address}
        </p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MetricBadge label={t('portfolioDoctor.table.monthlyCF')} value={fmtMoney(property.monthly_cash_flow, { sign: true })}
            tone={(property.monthly_cash_flow || 0) >= 0 ? 'positive' : 'negative'} />
          <MetricBadge label={t('portfolioDoctor.table.roi')} value={fmtPct(property.roi, 1)} />
          <MetricBadge label={t('portfolioDoctor.table.capRate')} value={fmtPct(property.cap_rate, 1)} />
        </div>
        {impactLabel && (
          <p className="text-xs text-slate-500 dark:text-slate-400 italic mb-4 leading-relaxed">{impactLabel}</p>
        )}
        <Button
          data-testid={`${testId}-cta`}
          onClick={onCta}
          className={`w-full rounded-xl font-semibold ${
            isStrong
              ? 'bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B]'
              : 'bg-[#12355B] hover:bg-[#1565C0] text-white'
          }`}
        >
          {cta}
        </Button>
      </div>
    </div>
  );
}

function MetricBadge({ label, value, tone }) {
  const toneCls =
    tone === 'positive' ? 'text-emerald-500' :
    tone === 'negative' ? 'text-red-500' :
    'text-[#12355B] dark:text-white';
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-2.5 text-center">
      <p className="text-[9px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-sm font-primary font-bold ${toneCls}`}>{value}</p>
    </div>
  );
}

function DebtCard({ t, data }) {
  if (!data) return null;
  const interp = data.interpretation;
  const sev = SEVERITY_ICON[interp.severity] || SEVERITY_ICON.gray;
  const Icon = sev.icon;
  return (
    <div data-testid="debt-analysis" className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card">
      <SectionHeader icon={Shield} title={t('portfolioDoctor.debt.title')} />
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t('portfolioDoctor.debt.totalDebt')}</p>
          <p className="text-xl font-primary font-extrabold text-[#12355B] dark:text-white mt-1">{fmtMoney(data.total_debt, { compact: true })}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t('portfolioDoctor.metrics.portfolioLtv')}</p>
          <p className="text-xl font-primary font-extrabold text-[#12355B] dark:text-white mt-1">{fmtPct(data.portfolio_ltv, 0)}</p>
        </div>
        {data.avg_interest_rate !== null && data.avg_interest_rate !== undefined && (
          <div className="col-span-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t('portfolioDoctor.debt.avgInterestRate')}</p>
            <p className="text-base font-semibold text-[#12355B] dark:text-white mt-1">{fmtPct(data.avg_interest_rate, 2)}</p>
          </div>
        )}
      </div>
      <div className={`flex items-start gap-2 px-3 py-3 rounded-xl border ${
        interp.severity === 'green' ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/30' :
        interp.severity === 'yellow' ? 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800/30' :
        interp.severity === 'red' ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800/30' :
        'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
      }`}>
        <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${sev.cls}`} />
        <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-200">
          {t(`portfolioDoctor.debt.${interp.key}`, interp.values || {})}
        </p>
      </div>
      {data.highest_debt_property && (
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
            {t('portfolioDoctor.debt.highestDebt')}
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-200 truncate">{data.highest_debt_property.address}</p>
          <p className="text-xs text-slate-400 mt-0.5">
            {fmtMoney(data.highest_debt_property.debt, { compact: true })} · {data.highest_debt_property.share_percent}% {t('portfolioDoctor.debt.shareOfDebt')}
          </p>
        </div>
      )}
    </div>
  );
}

function EquityCard({ t, data }) {
  if (!data) return null;
  return (
    <div data-testid="equity-analysis" className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card">
      <SectionHeader icon={TrendingUp} title={t('portfolioDoctor.equity.title')} />
      <div className="space-y-2 mb-4">
        <EquityRow label={t('portfolioDoctor.equity.totalValue')} value={fmtMoney(data.total_property_value, { compact: true })} />
        <EquityRow label={t('portfolioDoctor.equity.totalDebt')} value={`- ${fmtMoney(data.total_debt, { compact: true })}`} tone="negative" />
        <div className="border-t border-slate-100 dark:border-slate-800 pt-2">
          <EquityRow label={t('portfolioDoctor.equity.totalEquity')} value={fmtMoney(data.total_equity, { compact: true })} strong />
        </div>
        <EquityRow label={t('portfolioDoctor.equity.share')} value={fmtPct(data.equity_share_percent, 1)} />
      </div>
      <div className="grid grid-cols-1 gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
        {data.highest_equity && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              {t('portfolioDoctor.equity.highest')}
            </p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-700 dark:text-slate-200 truncate max-w-[65%]">{data.highest_equity.address}</span>
              <span className="font-semibold text-emerald-500">{fmtMoney(data.highest_equity.equity, { compact: true })}</span>
            </div>
          </div>
        )}
        {data.lowest_equity && data.lowest_equity.deal_id !== data.highest_equity?.deal_id && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              {t('portfolioDoctor.equity.lowest')}
            </p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-700 dark:text-slate-200 truncate max-w-[65%]">{data.lowest_equity.address}</span>
              <span className="font-semibold text-slate-500">{fmtMoney(data.lowest_equity.equity, { compact: true })}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EquityRow({ label, value, tone, strong }) {
  const toneCls =
    tone === 'negative' ? 'text-red-500' :
    strong ? 'text-[#12355B] dark:text-white' :
    'text-slate-700 dark:text-slate-300';
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`text-sm font-primary ${strong ? 'font-extrabold' : 'font-semibold'} ${toneCls}`}>{value}</span>
    </div>
  );
}
